import React, { useState, useCallback, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import { useAuth } from "../contexts/AuthContext";
import { useAnalytics } from "../utils/useAnalytics";
import {
  buildSavedSingleGoogleMapsURL,
  buildSavedPairGoogleMapsURL,
} from "../utils/mapUtils";
import { useTheme } from "../contexts/ThemeContext";
import { useDemoLoad } from "../contexts/DemoLoadContext";
import { formatDate } from "../utils/formatting";
import AISidebarDemo from "../components/AISidebarDemo";
import LoadDetailsModal from "../components/LoadDetailsModal";
import { getDataSourceBadge } from "../utils/formatting";

const getTruckTypeLabel = (type) => {
  const truckTypes = {
    V: "Van",
    SV: "Box Truck",
    F: "Flatbed",
    R: "Reefer",
    SD: "Step Deck",
    DD: "Double Drop",
    LB: "Lowboy",
    RGN: "Removable Gooseneck",
    TNK: "Tanker",
    AC: "Auto Carrier",
    CONT: "Container",
    DT: "Dump Truck",
    HB: "Hopper Bottom",
    PO: "Power Only",
    HS: "Hot Shot",
  };
  return truckTypes[type] || type;
};

const getDistance = (load) => {
  if (load.distance) {
    return `${Math.round(load.distance).toLocaleString()} mi`;
  }
  if (load.length) {
    return `${Math.round(load.length).toLocaleString()} mi`;
  }
  return "Distance unknown";
};

function formatCurrency(amount) {
  const num = Number(amount);
  if (!isFinite(num)) return "N/A";
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

const SavedLoads = () => {
  const { hideDemoLoad, setHideDemoLoad } = useDemoLoad();
  const { user } = useAuth();
  const { trackLoadBook } = useAnalytics(user); // eslint-disable-line no-unused-vars
  const { theme } = useTheme();
  const [bookedLoads, setBookedLoads] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [savedLoads, setSavedLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [showOutbound, setShowOutbound] = useState({});
  const [suggesting, setSuggesting] = useState(null);
  const [carriers, setCarriers] = useState([]);
  const [bookingId, setBookingId] = useState(null); // eslint-disable-line no-unused-vars
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showGlobalOverlay, setShowGlobalOverlay] = useState(false);
  const [savingLoadId, setSavingLoadId] = useState(null);
  const [deletingLoadId, setDeletingLoadId] = useState(null); // eslint-disable-line no-unused-vars
  const [saveMessage, setSaveMessage] = useState(""); // eslint-disable-line no-unused-vars
  const [saveMessageType, setSaveMessageType] = useState(""); // eslint-disable-line no-unused-vars

  const fetchBookedLoads = useCallback(
    async (refresh = true) => {
      try {
        const response = await axios.get(`${API_URL}/booked-loads/`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        });

        if (response.status === 200) {
          if (user?.email === "loadfinder@cinesis.ai") {
            let loads = [...response.data];
            if (!hideDemoLoad) {
              loads.push(demoLoad);
            }
            setBookedLoads(loads);
          } else {
            setBookedLoads(response.data);
          }
        }
        if (refresh) setLoading(false);
      } catch (err) {
        if (refresh) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, hideDemoLoad]
  );
  const navigate = useNavigate();
  const demoLoad = {
    _id: "test",
    id: "loadfinder@cinesis.ai-524475330",
    origin_city: "Compton",
    origin_state: "CA",
    destination_city: "Shepherdsville",
    destination_state: "KY",
    distance: 578.0,
    pay_rate: 4604.41,
    rate_per_mile_est: 7.97,
    weight: 35907.0,
    equipment_type: "Van",
    other_trailer_types: null,
    ship_date: "2025-10-03",
    booking_date: new Date("2025-10-03"),
    data_source: "C.H. Robinson",
    green_light: false,
    full_load: true,
    dead_head: 0.0,
    receive_date: null,
    booked_by: "loadfinder@cinesis.ai",
    status: "booked",
    is_pair: null,
    child: null,
    load_for: "loadfinder@cinesis.ai",
    assign_to: "",
    phone_number: "855-222-2931",
    entry_id: "demo-test",
  };
  const showAISidebar = user?.email === "loadfinder@cinesis.ai";
  const fetchSavedLoads = useCallback(
    async (refresh = true) => {
      try {
        if (refresh) {
          setLoading(true);
        }
        const savedResponse = await axios.get(`${API_URL}/loads/saved`);

        if (user?.account_type === "carrier") {
          // Also fetch loads suggested to this carrier
          const suggestedResponse = await axios.get(
            `${API_URL}/loads/suggested-to-me`
          );
          // Combine saved and suggested loads, removing duplicates based on entry_id
          let allLoads = [...savedResponse.data, ...suggestedResponse.data];
          const uniqueLoads = allLoads.filter(
            (load, index, self) =>
              index === self.findIndex((l) => l.entry_id === load.entry_id)
          );
          setSavedLoads(uniqueLoads);
        } else {
          setSavedLoads(savedResponse.data);
        }
      } catch (err) {
        console.error("Error fetching saved loads:", err);
        alert(
          err.response?.data?.detail ||
            "Failed to fetch saved loads. Please try again."
        );
      } finally {
        if (refresh) {
          setLoading(false);
        }
      }
    },
    [user]
  );

  const fetchDrivers = useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      const response = await axios.get(`${API_URL}/user/drivers/saved`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDrivers(response.data);
    } catch (err) {
      console.error("Error fetching saved drivers:", err);
    }
  }, []);

  const fetchCarriers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/user/carriers`);
      setCarriers(response.data);
    } catch (err) {
      console.error("Error fetching carriers:", err);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    const fetchInitialData = async () => {
      await Promise.all([
        fetchSavedLoads(),
        fetchBookedLoads(),
        fetchDrivers(),
        user?.account_type === "dispatcher"
          ? fetchCarriers()
          : Promise.resolve(),
      ]);
    };

    fetchInitialData();

    const intervalId = setInterval(() => {
      fetchSavedLoads(false);
      fetchBookedLoads(false);
      fetchDrivers();
      if (user?.account_type === "dispatcher") {
        fetchCarriers();
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [
    user,
    navigate,
    fetchSavedLoads,
    fetchBookedLoads,
    fetchDrivers,
    fetchCarriers,
  ]);

  useEffect(() => {
    const initialShowOutbound = savedLoads.reduce((acc, load) => {
      acc[load.entry_id] = true;
      return acc;
    }, {});
    setShowOutbound(initialShowOutbound);
  }, [savedLoads]);

  const handleAssignLoad = async (load, driverEmail) => {
    try {
      setSuggesting(load.id);
      const token = localStorage.getItem("access_token");

      await axios.put(
        `${API_URL}/booked-loads/${load.id}`,
        { assign_to: driverEmail },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setBookedLoads((prevLoads) =>
        prevLoads.map((l) =>
          l.id === load.id ? { ...l, assign_to: driverEmail } : l
        )
      );

      alert(
        driverEmail === ""
          ? "Assignment cancelled successfully!"
          : "Load assigned successfully!"
      );
    } catch (err) {
      console.error("Error assigning load:", err);
      alert("Failed to assign load. Please try again.");
    } finally {
      setSuggesting(null);
    }
  };

  const handleDeleteLoad = async (load) => {
    if (load.entry_id === "demo-test") {
      setBookedLoads((prev) => prev.filter((l) => l.entry_id !== "demo-test"));
      setHideDemoLoad(true);
      return;
    }
    if (!window.confirm("Are you sure you want to delete this saved load?"))
      return;

    try {
      setDeletingId(load.entry_id);

      await axios.delete(`${API_URL}/loads/${load.entry_id}`);

      // Remove from state
      setSavedLoads(savedLoads.filter((l) => l.entry_id !== load.entry_id));
    } catch (err) {
      console.error("Error deleting load:", err);
      alert("Failed to delete load. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  // Handler functions for LoadDetailsModal
  const handleLoadClick = (load) => {
    setSelectedLoad(load);
    setShowSidebar(true);
    setShowGlobalOverlay(true);
  };

  const handleSaveLoad = async (load) => {
    // This is already saved, so we don't need to do anything
    // or we could show a message that it's already saved
    setSaveMessage("Load is already saved!");
    setSaveMessageType("info");
  };

  const handleEmailDocuments = async (e, load) => {
    // Implement email documents functionality if needed
    console.log("Email documents for load:", load);
  };

  const handleBookLoad = async (load) => {
    if (!user?.email) {
      alert("Please log in to book loads");
      return;
    }

    try {
      setSavingLoadId(load.id);
      setSaveMessage("Loading profile...");
      setSaveMessageType("info");

      // Fetch user profile to get ch_t_number
      const token = localStorage.getItem("access_token");
      const profileResponse = await axios.get(`${API_URL}/user/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const userProfile = profileResponse.data;

      if (!userProfile?.ch_t_number) {
        alert(
          "Please add your CH Robinson T-Number in your profile to book loads. Go to Profile > Carrier Profile to add your T-Number."
        );
        setSavingLoadId(null);
        return;
      }

      setSaveMessage("Booking load...");
      setSaveMessageType("info");

      const response = await axios.post(
        `${API_URL}/load-book/book`,
        {
          loads: [
            {
              id: load.id,
              cost_type: load.cost_type || "Flat",
              code: load.code || "400",
              cost_description: load.cost_description || "Line Haul",
              sourceCostPerUnit:
                load.rate_per_mile_est || load.sourceCostPerUnit || 0,
              units: load.units || 1,
            },
          ],
        },
        {
          params: {
            carrier_code: userProfile.ch_t_number,
            carrier_email: user.email,
            carrier_name: user.name || "Carrier",
          },
        }
      );

      if (response.data.success) {
        alert("Load booked successfully!");
        setSaveMessage("Load booked successfully!");
        setSaveMessageType("success");
      } else {
        alert("Failed to book load. Please try again.");
        setSaveMessage("Failed to book load. Please try again.");
        setSaveMessageType("error");
      }
    } catch (error) {
      console.error("Error booking load:", error);
      const errorMessage =
        error.response?.data?.detail ||
        "Failed to book load. Please try again.";
      alert(`Booking Error: ${errorMessage}`);
      setSaveMessage(errorMessage);
      setSaveMessageType("error");
    } finally {
      setSavingLoadId(null);
    }
  };

  const handleDiscussLoad = (load) => {
    sessionStorage.setItem("selectedLoad", JSON.stringify(load));
    navigate("/ai-voice-agent");
  };

  const isLoadSaved = (load) => {
    // All loads in SavedLoads are already saved
    return true;
  };

  const handleSuggestLoad = async (load, carrierEmail) => {
    // If carrierEmail is "cancel", we're canceling the suggestion
    if (carrierEmail === "cancel") {
      try {
        setSuggesting(load.entry_id);

        const updateData = {
          suggested_to: null,
          suggested_by: null,
          load_status: null,
        };

        await axios.put(
          `${API_URL}/loads/update/${encodeURIComponent(load.entry_id)}`,
          updateData
        );
        alert("Suggestion cancelled successfully!");

        // Refresh the loads list to show updated status
        fetchSavedLoads();
      } catch (err) {
        console.error("Error cancelling suggestion:", err);
        alert(
          err.response?.data?.detail ||
            "Failed to cancel suggestion. Please try again."
        );
      } finally {
        setSuggesting(null);
      }
      return;
    }

    const carrier = carriers.find((c) => c.email === carrierEmail);
    if (!carrier) {
      alert("Please select a carrier");
      return;
    }

    try {
      setSuggesting(load.entry_id);

      // Update existing load with suggestion details
      const updateData = {
        suggested_to: carrier.email,
        suggested_by: user.email,
        load_status: "not approved yet",
      };

      await axios.put(
        `${API_URL}/loads/update/${encodeURIComponent(load.entry_id)}`,
        updateData
      );
      alert("Load suggested successfully!");

      // Refresh the loads list to show updated status
      fetchSavedLoads();
    } catch (err) {
      console.error("Error suggesting load:", err);
      alert(
        err.response?.data?.detail ||
          "Failed to suggest load. Please try again."
      );
    } finally {
      setSuggesting(null);
    }
  };

  const getHeaderColor = (load) => {
    if (!load.load_status) {
      if (load.is_pair) {
        return "var(--accent-purple)";
      } else {
        return "#2196f3";
      }
    }
    switch (load.load_status) {
      case "not approved yet":
        return "#FFD700"; // Yellow
      case "approved":
        return "#4CAF50"; // Green
      case "rejected":
        return "#FF4444"; // Red
      default:
        return "var(--primary)";
    }
  };

  // Add this function to handle toggling between outbound and return load
  const toggleLoadView = (loadId) => {
    setShowOutbound((prev) => ({
      ...prev,
      [loadId]: !prev[loadId],
    }));
  };

  if (loading)
    return (
      <div style={{ textAlign: "center", padding: "50px 0" }}>
        <i
          className="fas fa-truck fa-spin"
          style={{
            fontSize: "3rem",
            color: "var(--primary)",
            marginBottom: "20px",
          }}
        ></i>
        <p>Loading your saved loads...</p>
      </div>
    );
  if (error)
    return (
      <div
        style={{
          color: "var(--danger)",
          padding: "20px",
          border: "1px solid var(--danger)",
          borderRadius: "8px",
          textAlign: "center",
        }}
      >
        <i
          className="fas fa-exclamation-triangle"
          style={{ fontSize: "2rem", marginBottom: "10px" }}
        ></i>
        <p>{error}</p>
      </div>
    );
  return (
    <div style={{ maxWidth: 1400, margin: "0 auto", padding: 20 }}>
      <p style={{ color: "var(--text)", marginBottom: 20 }}>
        <i className="fas fa-info-circle" style={{ marginRight: 8 }}></i>
        {savedLoads.length === 1
          ? `${savedLoads.length} saved load.`
          : `${savedLoads.length} saved loads.`}
      </p>

      {loading ? (
        <div style={{ textAlign: "center", padding: 20 }}>
          <i className="fas fa-spinner fa-spin fa-2x"></i>
        </div>
      ) : savedLoads.length === 0 ? (
        <div style={{ textAlign: "center", padding: 20 }}>
          <p>
            No saved loads yet. Start searching for loads to save them here!
          </p>
          <Link
            to="/load-search"
            style={{ color: "var(--primary)", textDecoration: "none" }}
          >
            Go to Load Search
          </Link>
        </div>
      ) : (
        savedLoads.length > 0 && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: showAISidebar ? "1fr 340px" : "1fr",
              gap: showAISidebar ? 16 : 0,
              alignItems: "start",
            }}
          >
            {/* Left: lists/tables */}
            <div style={{ width: "100%" }}>
              <RegularLoadsSection
                savedLoads={savedLoads}
                getHeaderColor={getHeaderColor}
                user={user}
                handleSuggestLoad={handleSuggestLoad}
                suggesting={suggesting}
                carriers={carriers}
                handleDeleteLoad={handleDeleteLoad}
                deletingId={deletingId}
                showOutbound={showOutbound}
                toggleLoadView={toggleLoadView}
                handleBookLoad={handleBookLoad}
                bookingId={bookingId}
                handleLoadClick={handleLoadClick}
                savingLoadId={savingLoadId}
                theme={theme}
              />

              <BookedLoadsSection
                bookedLoads={bookedLoads}
                showOutbound={showOutbound}
                toggleLoadView={toggleLoadView}
                user={user}
                handleAssignLoad={handleAssignLoad}
                suggesting={suggesting}
                drivers={drivers}
                handleDeleteLoad={handleDeleteLoad}
                deletingId={deletingId}
                theme={theme}
              />
            </div>

            {/* Right: sticky AI sidebar */}
            {showAISidebar && (
              <div style={{ position: "sticky", top: 16, alignSelf: "start" }}>
                <AISidebarDemo
                  bookedLoads={bookedLoads}
                  onAdopt={(load) => {
                    alert(
                      `AI assigned: ${load.origin_city}, ${load.origin_state} → ${load.destination_city}, ${load.destination_state}`
                    );
                  }}
                />
              </div>
            )}
          </div>
        )
      )}

      {/* Load Details Modal */}
      {showSidebar && (
        <>
          {showGlobalOverlay && (
            <div
              onClick={() => {
                setShowSidebar(false);
                setShowGlobalOverlay(false);
              }}
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100vh",
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                zIndex: 999,
                backdropFilter: "blur(2px)",
              }}
            />
          )}
          <LoadDetailsModal
            selectedLoad={selectedLoad}
            showSidebar={showSidebar}
            setShowSidebar={setShowSidebar}
            setShowGlobalOverlay={setShowGlobalOverlay}
            searchParams={{}} // Not needed for SavedLoads
            theme={theme}
            savingLoadId={savingLoadId}
            deletingLoadId={deletingLoadId}
            isLoadSaved={isLoadSaved}
            handleEmailDocuments={handleEmailDocuments}
            handleSaveLoad={handleSaveLoad}
            handleDeleteLoad={handleDeleteLoad}
            handleBookLoad={handleBookLoad}
            handleDiscussLoad={handleDiscussLoad}
          />
        </>
      )}
    </div>
  );
};

export default SavedLoads;

function BookedLoadsSection({
  bookedLoads,
  showOutbound,
  toggleLoadView,
  user,
  handleAssignLoad,
  suggesting,
  drivers,
  handleDeleteLoad,
  deletingId,
  theme,
}) {
  return (
    <div style={{ marginBottom: "40px" }}>
      <h3
        style={{
          color: "var(--text-secondary)",
          borderBottom: "2px solid var(--chrome)",
          paddingBottom: "10px",
          marginBottom: "20px",
        }}
      >
        <i className="fas fa-truck" style={{ marginRight: "10px" }}></i>
        Booked Loads
      </h3>
      <div
        style={{
          overflowX: "auto",
          overflowY: "auto",
          maxHeight: "70vh",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            marginTop: "20px",
            borderRadius: "8px",
            boxShadow: "var(--shadow)",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: "var(--surface)",
                color: "var(--text)",
              }}
            >
              <th
                style={{
                  padding: "8px 12px",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: "var(--surface)",
                }}
              >
                Origin
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: "var(--surface)",
                }}
              >
                Destination
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: "var(--surface)",
                }}
              >
                Price
                <br />
                RpM
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: "var(--surface)",
                }}
              >
                Distance
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: "var(--surface)",
                }}
              >
                Weight
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: "var(--surface)",
                }}
              >
                Equipment
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: "var(--surface)",
                }}
              >
                Ship Date
              </th>
              <th
                style={{
                  padding: "8px 12px",
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: "var(--surface)",
                }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {bookedLoads
              .filter((load) => !load.assign_to)
              .map((load) => {
                const currentLoad =
                  showOutbound[load.id] !== false ? load : load.child || load;
                return (
                  <tr
                    key={load.id}
                    style={{
                      borderBottom: "1px solid var(--border)",
                      backgroundColor: load.is_pair
                        ? "var(--accent-purple-subtle)"
                        : "transparent",
                    }}
                  >
                    <td style={{ padding: "8px 12px" }}>
                      {currentLoad.origin_city}, {currentLoad.origin_state}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {currentLoad.destination_city},{" "}
                      {currentLoad.destination_state}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {formatCurrency(
                        currentLoad.rate_per_mile_est * currentLoad.distance
                      )}
                      <br />${currentLoad.rate_per_mile_est?.toFixed(2)}/mi
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {getDistance(currentLoad)}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {currentLoad.weight?.toLocaleString() || 0} lbs
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {getTruckTypeLabel(currentLoad.equipment_type)}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {formatDate(load.ship_date)}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      <div style={{ display: "flex", gap: "8px" }}>
                        {user?.account_type === "carrier" && (
                          <select
                            onChange={(e) =>
                              handleAssignLoad(load, e.target.value)
                            }
                            disabled={suggesting === load.id}
                            value={load.assign_to || ""}
                            style={{
                              padding: "4px 8px",
                              borderRadius: "4px",
                              border: "1px solid var(--border)",
                              backgroundColor: "var(--dark)",
                              color: "var(--text)",
                              cursor: "pointer",
                            }}
                          >
                            <option value="">Assign...</option>
                            {drivers.map((driver) => (
                              <option
                                key={driver.driver_email}
                                value={driver.driver_email}
                              >
                                {driver.driver_name}
                              </option>
                            ))}
                          </select>
                        )}

                        <a
                          href={
                            load.is_pair
                              ? buildSavedPairGoogleMapsURL(load, load.child)
                              : buildSavedSingleGoogleMapsURL(load)
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: "var(--accent-blue)" }}
                        >
                          <i className="fas fa-map-marked-alt"></i>
                        </a>

                        <button
                          onClick={() => handleDeleteLoad(load)}
                          disabled={deletingId === load.id}
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--danger)",
                            cursor: "pointer",
                            padding: "0",
                          }}
                        >
                          {deletingId === load.id ? (
                            <i className="fas fa-spinner fa-spin" />
                          ) : (
                            <i className="fas fa-trash" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RegularLoadsSection({
  savedLoads,
  getHeaderColor, // (unused now, but leaving in case you style later)
  user,
  handleSuggestLoad,
  suggesting,
  carriers,
  handleDeleteLoad,
  deletingId,
  showOutbound,
  toggleLoadView,
  handleBookLoad,
  bookingId,
  handleLoadClick,
  savingLoadId,
  theme,
}) {
  return (
    <div>
      <h2 style={{ marginBottom: "20px" }}>
        <div className="fas fa-bookmark"></div> Bookmarked Loads
      </h2>

      {savedLoads.some((load) => !load.suggested_to) ? (
        <div
          style={{ overflowX: "auto", overflowY: "auto", maxHeight: "70vh" }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: "20px",
              borderRadius: "8px",
              boxShadow: "var(--shadow)",
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: "var(--surface)",
                  color: "var(--text)",
                }}
              >
                <th
                  style={{
                    padding: "8px 12px",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "var(--surface)",
                  }}
                >
                  Origin
                </th>
                <th
                  style={{
                    padding: "8px 12px",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "var(--surface)",
                  }}
                >
                  Destination
                </th>
                <th
                  style={{
                    padding: "8px 12px",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "var(--surface)",
                  }}
                >
                  Price
                  <br />
                  RpM
                </th>
                <th
                  style={{
                    padding: "8px 12px",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "var(--surface)",
                  }}
                >
                  Distance
                </th>
                <th
                  style={{
                    padding: "8px 12px",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "var(--surface)",
                  }}
                >
                  Weight
                </th>
                <th
                  style={{
                    padding: "8px 12px",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "var(--surface)",
                  }}
                >
                  Equipment
                </th>
                <th
                  style={{
                    padding: "8px 12px",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "var(--surface)",
                  }}
                >
                  Source
                </th>
                <th
                  style={{
                    padding: "8px 12px",
                    position: "sticky",
                    top: 0,
                    zIndex: 1,
                    backgroundColor: "var(--surface)",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {savedLoads
                .filter((load) => !load.suggested_to)
                .map((load) => {
                  const currentLoad =
                    showOutbound[load.entry_id] !== false
                      ? load
                      : load.child || load;

                  return (
                    <tr
                      key={load.entry_id}
                      onClick={() => handleLoadClick(currentLoad)}
                      style={{
                        borderBottom: "1px solid var(--border)",
                        backgroundColor: load.is_pair
                          ? "var(--accent-purple-subtle)"
                          : "transparent",
                        cursor: "pointer",
                      }}
                    >
                      <td style={{ padding: "8px 12px" }}>
                        {currentLoad.origin_city}, {currentLoad.origin_state}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        {currentLoad.destination_city},{" "}
                        {currentLoad.destination_state}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        {formatCurrency(
                          currentLoad?.payment ??
                            (currentLoad?.rate_per_mile_est || 0) *
                              (currentLoad?.distance + currentLoad?.dead_head ||
                                0)
                        )}
                        <br />$
                        {currentLoad.rate_per_mile_est?.toFixed(2) || "0.00"}/mi
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        {getDistance(currentLoad)}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        {currentLoad.weight?.toLocaleString() || 0} lbs
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        {getTruckTypeLabel(currentLoad.equipment_type)}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        {getDataSourceBadge(load.data_source, theme)}
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                          }}
                        >
                          {user?.account_type === "dispatcher" && (
                            <select
                              onChange={(e) => {
                                e.stopPropagation();
                                handleSuggestLoad(load, e.target.value);
                              }}
                              disabled={suggesting === load.entry_id}
                              style={{
                                padding: "4px 8px",
                                borderRadius: 4,
                                border: "1px solid var(--border)",
                                backgroundColor: "var(--dark)",
                                color: "var(--text)",
                                cursor: "pointer",
                              }}
                            >
                              <option value="">Suggest to...</option>
                              {carriers.map((carrier) => (
                                <option
                                  key={carrier.email}
                                  value={carrier.email}
                                >
                                  {carrier.company_name || carrier.email}
                                </option>
                              ))}
                              {load.suggested_to && (
                                <option value="cancel">
                                  Cancel Suggestion
                                </option>
                              )}
                            </select>
                          )}

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBookLoad(load);
                            }}
                            disabled={
                              savingLoadId === load.id ||
                              bookingId === load.entry_id
                            }
                            title="Book"
                            style={{
                              background: "none",
                              border: "none",
                              color:
                                savingLoadId === load.id
                                  ? "var(--text-muted)"
                                  : "var(--primary)",
                              cursor:
                                savingLoadId === load.id ? "wait" : "pointer",
                              padding: 0,
                            }}
                          >
                            {savingLoadId === load.id ||
                            bookingId === load.entry_id ? (
                              <i className="fas fa-spinner fa-spin" />
                            ) : (
                              <i className="fas fa-check" />
                            )}
                          </button>

                          <a
                            href={
                              load.is_pair
                                ? buildSavedPairGoogleMapsURL(load, load.child)
                                : buildSavedSingleGoogleMapsURL(load)
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View Route"
                            style={{ color: "var(--accent-blue)" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <i className="fas fa-map-marked-alt"></i>
                          </a>

                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteLoad(load);
                            }}
                            disabled={deletingId === load.entry_id}
                            title="Delete"
                            style={{
                              background: "none",
                              border: "none",
                              color: "var(--danger)",
                              cursor: "pointer",
                              padding: 0,
                            }}
                          >
                            {deletingId === load.entry_id ? (
                              <i className="fas fa-spinner fa-spin" />
                            ) : (
                              <i className="fas fa-trash" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "20px",
            backgroundColor: "var(--surface)",
            borderRadius: "8px",
            color: "var(--text-muted)",
          }}
        >
          No regular loads
        </div>
      )}
    </div>
  );
}
